/** Shared goal / progress chrome for Gauge, KPI, and related metric widgets. */

import { escapeHtml } from './format';

export interface GoalProgress {
    value: number;
    goal: number;
    /** Percent of goal; may exceed 100. */
    pctOfGoal: number;
    /** 0–100 fill for arcs / bars (clamped). */
    fillPct: number;
    remaining: number;
    isOver: boolean;
    /** e.g. "1,234 remaining" or "1,234 over goal" */
    statusLabel: string;
    /** e.g. "85% of goal" */
    pctLabel: string;
    /** The goal in the measure's own format, e.g. "£10,000.00". */
    goalLabel: string;
}

/** Formats a number the way the measure is shown (currency, decimals, abbreviation). */
export type NumberFormatter = (value: number) => string;

export function formatMetricNumber(n: number): string {
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function computeGoalProgress(value: number, goal: number, format: NumberFormatter = formatMetricNumber): GoalProgress | null {
    if (!Number.isFinite(value) || !Number.isFinite(goal) || goal <= 0) return null;
    const remaining = goal - value;
    const isOver = remaining <= 0;
    const pctOfGoal = (value / goal) * 100;
    const fillPct = Math.min(100, Math.max(0, pctOfGoal));
    const absGap = Math.abs(remaining);
    return {
        value,
        goal,
        pctOfGoal,
        fillPct,
        remaining,
        isOver,
        statusLabel: isOver
            ? `${format(absGap)} over goal`
            : `${format(absGap)} remaining`,
        pctLabel: `${Math.round(pctOfGoal).toLocaleString()}% of goal`,
        goalLabel: format(goal),
    };
}

/** Linear goal track using --gauge-pct (no inline width). */
export function goalTrackHtml(progress: GoalProgress, toneClass = ''): string {
    const tone = progress.isOver ? 'analytics-widget__goal-track--over' : toneClass;
    return `<div class="analytics-widget__goal-track ${tone}" role="progressbar"
        aria-valuenow="${progress.value}" aria-valuemin="0" aria-valuemax="${progress.goal}"
        aria-valuetext="${escapeHtml(progress.pctLabel)}" aria-label="Progress to goal"
        style="--gauge-pct:${progress.fillPct}">
        <div class="analytics-widget__goal-fill"></div>
    </div>`;
}

export function goalMetaHtml(progress: GoalProgress): string {
    return `<div class="analytics-widget__goal-meta">
        <span class="analytics-widget__goal-caption">Goal: ${escapeHtml(progress.goalLabel)}</span>
        <span class="analytics-widget__goal-status">${escapeHtml(progress.statusLabel)}</span>
        <span class="analytics-widget__goal-pct tabular-nums">${escapeHtml(progress.pctLabel)}</span>
    </div>`;
}

export interface Thresholds {
    warn?: number | null;
    danger?: number | null;
    /**
     * Which way is good. Absent: inferred, so a danger level above the warning level (for example open
     * incidents: warn at 10, danger at 20) means lower is better, and otherwise higher is better.
     */
    direction?: 'higherIsBetter' | 'lowerIsBetter' | null;
}

export type ThresholdState = 'success' | 'warning' | 'danger' | null;

/** Tone for a value against warning and danger levels, in the direction that is good for this measure. */
export function thresholdState(value: number | null | undefined, thresholds?: Thresholds | null): ThresholdState {
    if (value == null || !Number.isFinite(value) || !thresholds) return null;
    const warn = Number.isFinite(thresholds.warn as number) ? thresholds.warn as number : null;
    const danger = Number.isFinite(thresholds.danger as number) ? thresholds.danger as number : null;
    if (warn == null && danger == null) return null;
    const lowerIsBetter = thresholds.direction ? thresholds.direction === 'lowerIsBetter' : warn != null && danger != null && danger > warn;
    const breaches = (level: number | null) => level != null && (lowerIsBetter ? value >= level : value <= level);
    if (breaches(danger)) return 'danger';
    if (breaches(warn)) return 'warning';
    return 'success';
}
